import React from 'react'
import LoadingPlaceholder from 'components/LoadingPlaceholder'
import getDisplayName from 'lib/get-display-name'
import Performance from 'lib/performance'

let uniqueId = 0
const pendingLoaders = new Set()

interface WithLoaderProps {
  /**
   * Whether the component is currently loading or not.
   */
  isLoading: boolean
  /**
   * Defines the rate at which the componentLoading metrics will be sampled.
   * To avoid the metric being logged, set to 0. To always log, set to 1.
   */
  timingSampleRate?: number
}

export default function withLoader<P>(Component: React.ComponentType<P>) {
  const componentName = getDisplayName(Component)

  return class WithLoader extends React.Component<WithLoaderProps & P> {
    static displayName = `WithLoader(${componentName})`

    loaderId: number | undefined
    shouldSample: boolean | undefined
    performance: Performance | undefined

    componentDidMount() {
      this.loaderId = uniqueId++
      // Don't use defaultProps because HOCs break typescript's defaultProps inferring
      const timingSampleRate: number =
        typeof this.props.timingSampleRate === 'number' ? this.props.timingSampleRate : 0.5

      this.shouldSample = Math.random() < timingSampleRate
      if (this.shouldSample) {
        this.performance = new Performance()
      }

      this.handleLoadingChange({}, this.props)
    }

    componentDidUpdate(prevProps: Partial<WithLoaderProps>) {
      this.handleLoadingChange(prevProps, this.props)
    }

    componentWillUnmount() {
      this.handleLoadEnd()
    }

    handleLoadingChange(prevProps: Partial<WithLoaderProps>, nextProps: Partial<WithLoaderProps>) {
      if (prevProps.isLoading && !nextProps.isLoading) {
        this.handleLoadEnd()
      }

      if (!prevProps.isLoading && nextProps.isLoading) {
        this.handleLoadStart()
      }
    }

    handleLoadStart() {
      if (this.performance && !pendingLoaders.has(this.loaderId) && this.shouldSample) {
        this.performance.startComponentTimer(`componentLoading`, componentName)
        pendingLoaders.add(this.loaderId)
      }
    }

    handleLoadEnd() {
      if (this.performance && pendingLoaders.has(this.loaderId) && this.shouldSample) {
        this.performance.endComponentTimer(`componentLoading`, componentName)
        pendingLoaders.delete(this.loaderId)
      }
    }

    render() {
      return (
        <LoadingPlaceholder isLoading={this.props.isLoading}>
          <Component {...this.props} />
        </LoadingPlaceholder>
      )
    }
  }
}
